import { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useCart } from '@/contexts/CartContext';
import { useMemberAuth } from '@/contexts/MemberAuthContext';

type NavbarProps = {
  bookingPath?: string;
};

export default function Navbar({ bookingPath = '/booking' }: NavbarProps) {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [memberMenuOpen, setMemberMenuOpen] = useState(false);
  const memberMenuRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { totalQuantity, toggleCart } = useCart();
  const { member, isLoggedIn, logout } = useMemberAuth();

  useEffect(() => {
    if (!memberMenuOpen) return;
    function handleClickOutside(event: MouseEvent) {
      if (memberMenuRef.current && !memberMenuRef.current.contains(event.target as Node)) {
        setMemberMenuOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [memberMenuOpen]);

  async function handleLogout() {
    setMemberMenuOpen(false);
    await logout();
    navigate('/');
  }

  const isHomePage = location.pathname === '/';

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 40);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { label: '首頁', path: '/', matchPaths: ['/'] },
    { label: '產品系列', path: '/products', matchPaths: ['/products', '/product/'] },
    { label: '聽力知識', path: '/knowledge', matchPaths: ['/knowledge', '/article/'] },
    { label: '常見問題', path: '/faq', matchPaths: ['/faq'] },
    { label: '最新消息', path: '/news', matchPaths: ['/news'] },
    { label: '關於我們', path: '/about', matchPaths: ['/about'] },
    { label: '共聽生活協會', path: '/association', matchPaths: ['/association'] },
    { label: '預約檢測', path: '/booking', matchPaths: ['/booking', '/booking-success'] },
  ];

  const isActive = (link: typeof navLinks[0]) => {
    return link.matchPaths.some((p) => {
      if (p === '/') return location.pathname === '/';
      return location.pathname.startsWith(p);
    });
  };

  const navBase =
    'fixed top-0 left-0 right-0 z-50 transition-all duration-300 backdrop-blur-md';
  const navBg =
    scrolled || !isHomePage
      ? 'bg-white/80 shadow-sm'
      : 'bg-white/40';

  return (
    <nav className={`${navBase} ${navBg}`}>
      <div className="w-full px-4 md:px-10 flex items-center justify-between h-14 md:h-20">
        <a
          href="/"
          className="flex items-center"
          onClick={(e) => {
            e.preventDefault();
            navigate('/');
          }}
        >
          <span className="flex text-lg md:text-2xl font-bold tracking-wide whitespace-nowrap">
            <span className="text-[#1B3D2A]">富聆聽</span>
            <span className="text-[#D4A574]">健康</span>
          </span>
        </a>

        {/* Desktop nav：8 個主選單項目 + 圖示 + 按鈕在中等寬度視窗（例如筆電縮小視窗、開著開發者工具）容易被擠出畫面，
            改到 xl（1280px）以上才顯示完整版，避免右側圖示被裁切；中等寬度一律走漢堡選單。 */}
        <div className="hidden xl:flex items-center gap-8">
          {navLinks.map((link) => {
            const active = isActive(link);
            return (
              <a
                key={link.path}
                href={link.path}
                onClick={(e) => {
                  e.preventDefault();
                  navigate(link.path);
                }}
                className={`relative transition-colors whitespace-nowrap ${
                  active
                    ? 'text-lg font-bold text-[#1B3D2A]'
                    : 'text-base font-medium text-[#5A6B5A] hover:text-[#1B3D2A]'
                }`}
              >
                {link.label}
                <span
                  className={`absolute -bottom-1.5 left-1/2 -translate-x-1/2 h-0.5 rounded-full bg-[#D4A574] transition-all duration-300 ease-out ${
                    active ? 'w-3/4 opacity-100' : 'w-0 opacity-0'
                  }`}
                />
              </a>
            );
          })}
        </div>

        <div className="hidden xl:flex items-center gap-3">
          <div className="relative" ref={memberMenuRef}>
            <button
              className="relative w-11 h-11 flex items-center justify-center rounded-full text-[#5A6B5A] hover:bg-[#1B3D2A]/8 hover:text-[#1B3D2A] transition-colors"
              onClick={() => setMemberMenuOpen((current) => !current)}
              aria-label="會員"
            >
              <i className="ri-user-line text-xl"></i>
            </button>
            {memberMenuOpen && isLoggedIn && (
              <div className="absolute right-0 top-full mt-2 w-56 rounded-2xl border border-[#E8E0D6] bg-white p-2 shadow-lg">
                <div className="border-b border-[#E8E0D6] px-3 py-2">
                  <p className="truncate text-sm font-bold text-[#1B3D2A]">{member?.Name}</p>
                  <p className="truncate text-xs text-[#9A8A7A]">{member?.Email}</p>
                </div>
                <button
                  className="mt-1 flex w-full items-center gap-2 rounded-xl px-3 py-2 text-left text-sm font-medium text-[#5A6B5A] hover:bg-[#FAF8F5] hover:text-[#1B3D2A]"
                  onClick={() => {
                    setMemberMenuOpen(false);
                    navigate('/account');
                  }}
                >
                  <i className="ri-user-settings-line" />
                  會員中心
                </button>
                <button
                  className="flex w-full items-center gap-2 rounded-xl px-3 py-2 text-left text-sm font-medium text-[#5A6B5A] hover:bg-[#FAF8F5] hover:text-[#1B3D2A]"
                  onClick={() => {
                    setMemberMenuOpen(false);
                    navigate('/account/orders');
                  }}
                >
                  <i className="ri-file-list-3-line" />
                  我的訂單
                </button>
                <button
                  className="flex w-full items-center gap-2 rounded-xl px-3 py-2 text-left text-sm font-medium text-[#c3423f] hover:bg-[#fdebea]"
                  onClick={handleLogout}
                >
                  <i className="ri-logout-box-line" />
                  登出
                </button>
              </div>
            )}
            {memberMenuOpen && !isLoggedIn && (
              <div className="absolute right-0 top-full mt-2 w-52 rounded-2xl border border-[#E8E0D6] bg-white p-2 shadow-lg">
                <button
                  className="flex w-full items-center gap-2 rounded-xl px-3 py-2 text-left text-sm font-medium text-[#5A6B5A] hover:bg-[#FAF8F5] hover:text-[#1B3D2A]"
                  onClick={() => {
                    setMemberMenuOpen(false);
                    navigate('/login');
                  }}
                >
                  <i className="ri-login-box-line" />
                  會員登入
                </button>
                <button
                  className="flex w-full items-center gap-2 rounded-xl px-3 py-2 text-left text-sm font-medium text-[#5A6B5A] hover:bg-[#FAF8F5] hover:text-[#1B3D2A]"
                  onClick={() => {
                    setMemberMenuOpen(false);
                    navigate('/register');
                  }}
                >
                  <i className="ri-user-add-line" />
                  會員註冊
                </button>
                <button
                  className="flex w-full items-center gap-2 rounded-xl px-3 py-2 text-left text-sm font-medium text-[#5A6B5A] hover:bg-[#FAF8F5] hover:text-[#1B3D2A]"
                  onClick={() => {
                    setMemberMenuOpen(false);
                    navigate('/forgot-password');
                  }}
                >
                  <i className="ri-lock-password-line" />
                  忘記密碼
                </button>
                <button
                  className="flex w-full items-center gap-2 rounded-xl px-3 py-2 text-left text-sm font-medium text-[#5A6B5A] hover:bg-[#FAF8F5] hover:text-[#1B3D2A]"
                  onClick={() => {
                    setMemberMenuOpen(false);
                    navigate('/resend-verification');
                  }}
                >
                  <i className="ri-mail-send-line" />
                  補寄認證信
                </button>
                <button
                  className="flex w-full items-center gap-2 rounded-xl px-3 py-2 text-left text-sm font-medium text-[#5A6B5A] hover:bg-[#FAF8F5] hover:text-[#1B3D2A]"
                  onClick={() => {
                    setMemberMenuOpen(false);
                    navigate('/order-lookup');
                  }}
                >
                  <i className="ri-file-search-line" />
                  非會員訂單查詢
                </button>
              </div>
            )}
          </div>
          <button
            className="relative w-11 h-11 flex items-center justify-center rounded-full text-[#5A6B5A] hover:bg-[#1B3D2A]/8 hover:text-[#1B3D2A] transition-colors"
            onClick={toggleCart}
            aria-label="購物車"
          >
            <i className="ri-shopping-cart-2-line text-xl"></i>
            {totalQuantity > 0 && (
              <span className="absolute top-1 right-1 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-[#D4A574] px-1 text-[10px] font-bold text-white">
                {totalQuantity > 99 ? '99+' : totalQuantity}
              </span>
            )}
          </button>
          <button
            className="bg-[#1B3D2A] text-white px-6 py-3 rounded-full text-base font-medium hover:bg-[#143026] transition-colors whitespace-nowrap"
            onClick={() => navigate(bookingPath)}
          >
            立即預約
          </button>
        </div>

        {/* Mobile / 中等寬度: cart + hamburger */}
        <div className="xl:hidden flex items-center gap-1">
          <button
            className="relative w-10 h-10 flex items-center justify-center text-[#5A6B5A]"
            onClick={toggleCart}
            aria-label="購物車"
          >
            <i className="ri-shopping-cart-2-line text-xl"></i>
            {totalQuantity > 0 && (
              <span className="absolute top-0.5 right-0.5 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-[#D4A574] px-1 text-[10px] font-bold text-white">
                {totalQuantity > 99 ? '99+' : totalQuantity}
              </span>
            )}
          </button>
          <button
            className="w-10 h-10 flex items-center justify-center text-[#5A6B5A]"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            <i className={`ri-${mobileMenuOpen ? 'close' : 'menu'}-line text-2xl`}></i>
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileMenuOpen && (
        <div className="xl:hidden bg-white border-t border-[#E8E0D6] px-4 py-4 shadow-lg max-h-[calc(100vh-3.5rem)] overflow-y-auto">
          {navLinks.map((link, index) => {
            const active = isActive(link);
            return (
              <a
                key={link.path}
                href={link.path}
                onClick={(e) => {
                  e.preventDefault();
                  navigate(link.path);
                  setMobileMenuOpen(false);
                }}
                className={`relative block py-3 pl-3 transition-all duration-300 ${
                  index !== navLinks.length - 1 ? 'border-b border-[#E8E0D6]' : ''
                } ${
                  active
                    ? 'text-lg font-bold text-[#1B3D2A] border-l-[3px] border-l-[#D4A574] bg-[#1B3D2A]/5'
                    : 'text-base font-medium text-[#5A6B5A] hover:text-[#1B3D2A] border-l-[3px] border-l-transparent'
                }`}
              >
                {link.label}
              </a>
            );
          })}
          <button
            className="mt-4 w-full bg-[#1B3D2A] text-white px-6 py-3 rounded-full text-base font-medium hover:bg-[#143026] transition-colors"
            onClick={() => {
              navigate(bookingPath);
              setMobileMenuOpen(false);
            }}
          >
            立即預約
          </button>

          <div className="mt-4 border-t border-[#E8E0D6] pt-4">
            {isLoggedIn ? (
              <>
                <p className="px-3 text-sm font-bold text-[#1B3D2A]">{member?.Name} 您好</p>
                <button
                  className="mt-2 flex w-full items-center gap-2 rounded-xl px-3 py-3 text-left text-base font-medium text-[#5A6B5A] hover:bg-[#FAF8F5] hover:text-[#1B3D2A]"
                  onClick={() => {
                    setMobileMenuOpen(false);
                    navigate('/account');
                  }}
                >
                  <i className="ri-user-settings-line" />
                  會員中心
                </button>
                <button
                  className="flex w-full items-center gap-2 rounded-xl px-3 py-3 text-left text-base font-medium text-[#5A6B5A] hover:bg-[#FAF8F5] hover:text-[#1B3D2A]"
                  onClick={() => {
                    setMobileMenuOpen(false);
                    navigate('/account/orders');
                  }}
                >
                  <i className="ri-file-list-3-line" />
                  我的訂單
                </button>
                <button
                  className="flex w-full items-center gap-2 rounded-xl px-3 py-3 text-left text-base font-medium text-[#c3423f] hover:bg-[#fdebea]"
                  onClick={() => {
                    setMobileMenuOpen(false);
                    handleLogout();
                  }}
                >
                  <i className="ri-logout-box-line" />
                  登出
                </button>
              </>
            ) : (
              <>
                <div className="flex gap-3 px-3">
                  <button
                    className="flex-1 rounded-full border border-[#1B3D2A] py-2.5 text-sm font-bold text-[#1B3D2A]"
                    onClick={() => {
                      setMobileMenuOpen(false);
                      navigate('/login');
                    }}
                  >
                    會員登入
                  </button>
                  <button
                    className="flex-1 rounded-full bg-[#1B3D2A] py-2.5 text-sm font-bold text-white"
                    onClick={() => {
                      setMobileMenuOpen(false);
                      navigate('/register');
                    }}
                  >
                    加入會員
                  </button>
                </div>
                <button
                  className="mt-3 flex w-full items-center gap-2 rounded-xl px-3 py-2.5 text-left text-sm font-medium text-[#5A6B5A] hover:bg-[#FAF8F5] hover:text-[#1B3D2A]"
                  onClick={() => {
                    setMobileMenuOpen(false);
                    navigate('/forgot-password');
                  }}
                >
                  <i className="ri-lock-password-line" />
                  忘記密碼
                </button>
                <button
                  className="flex w-full items-center gap-2 rounded-xl px-3 py-2.5 text-left text-sm font-medium text-[#5A6B5A] hover:bg-[#FAF8F5] hover:text-[#1B3D2A]"
                  onClick={() => {
                    setMobileMenuOpen(false);
                    navigate('/resend-verification');
                  }}
                >
                  <i className="ri-mail-send-line" />
                  補寄認證信
                </button>
                <button
                  className="flex w-full items-center gap-2 rounded-xl px-3 py-2.5 text-left text-sm font-medium text-[#5A6B5A] hover:bg-[#FAF8F5] hover:text-[#1B3D2A]"
                  onClick={() => {
                    setMobileMenuOpen(false);
                    navigate('/order-lookup');
                  }}
                >
                  <i className="ri-file-search-line" />
                  非會員訂單查詢
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </nav>
  );
}
